import { useLayoutEffect, type RefObject } from 'react';

/**
 * TUGMAGA BOG'LANGAN RO'YXATNING JOYI — `Select` va `Combobox` uchun umumiy.
 *
 * Ro'yxat portalda va `position: fixed` bilan chiziladi (oyna tanasining
 * `overflow` chegarasida kesilmasin). Joyi esa shu yerda hisoblanadi, ikki
 * komponentda ikki xil hisob bo'lib, biri tuzatilib ikkinchisi eskicha
 * qolmasin.
 */

const GAP = 4;
/** Chetdan qoldiriladigan bo'shliq — ro'yxat chetga yopishmasin. */
const EDGE = 8;
/** Joy juda tor bo'lsa ham kamida shuncha (≈2 qator) ko'rinsin. */
const MIN_LIST_HEIGHT = 88;

export interface AnchoredPlacement {
  left: number;
  width: number;
  maxWidth: number;
  /** Ro'yxatning (`listbox`) o'ziga beriladigan eng katta balandlik. */
  listMaxHeight: number;
  top?: number;
  bottom?: number;
}

export interface PlacementOptions {
  /** Joy yetarli bo'lganda ro'yxat balandligi. */
  maxListHeight: number;
  /** Panelning ro'yxatdan tashqaridagi qismi: ichki bo'shliq, chegara, qidiruv. */
  chrome: number;
}

/** Ko'rinib turgan soha — telefonda klaviatura ochilsa u `innerHeight` dan kichik. */
const visibleArea = (): { top: number; left: number; width: number; height: number } => {
  const vv = window.visualViewport;
  return vv === null || vv === undefined
    ? { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight }
    : { top: vv.offsetTop, left: vv.offsetLeft, width: vv.width, height: vv.height };
};

/**
 * Tugma o'zini o'rab turgan aylanuvchi konteyner (masalan oyna tanasi)
 * ichida ko'rinmay qoldimi. Shunda ro'yxat yopiladi: aks holda tugma
 * modal chegarasidan chiqib ketgan bo'lsa ham, ro'yxat unga ergashib
 * oynadan tashqarida "suzib" qolardi.
 */
export const isDetached = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  // O'lchami yo'q (yashirin yoki jsdom) — hukm chiqarib bo'lmaydi.
  if (rect.height === 0) return false;
  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (style.overflowY !== 'visible') {
      const box = node.getBoundingClientRect();
      if (box.height > 0 && (rect.bottom <= box.top || rect.top >= box.bottom)) return true;
    }
    // `fixed` qatlamdan (modal) yuqoridagilar uni kesmaydi.
    if (style.position === 'fixed') break;
  }
  return false;
};

export const computePlacement = (
  anchor: HTMLElement,
  { maxListHeight, chrome }: PlacementOptions,
): AnchoredPlacement => {
  const rect = anchor.getBoundingClientRect();
  const view = visibleArea();
  /* Oyna ichida bo'lsa chegara — OYNANING o'zi: ro'yxat modal panelidan
     tashqariga chiqib, uning ortidagi sahifa ustida osilib turmasin. */
  const dialog = anchor.closest('[role="dialog"]')?.getBoundingClientRect();
  const area =
    dialog === undefined || dialog.height === 0
      ? view
      : {
          top: Math.max(view.top, dialog.top),
          left: Math.max(view.left, dialog.left),
          width: Math.min(view.left + view.width, dialog.right) - Math.max(view.left, dialog.left),
          height: Math.min(view.top + view.height, dialog.bottom) - Math.max(view.top, dialog.top),
        };
  const below = area.top + area.height - rect.bottom - GAP - EDGE;
  const above = rect.top - area.top - GAP - EDGE;
  // Pastda joy yetmasa va tepada ko'proq bo'lsa, ro'yxat TEPAGA ochiladi.
  const flip = below < maxListHeight + chrome && above > below;
  /* Ro'yxat qolgan joyga SIG'ADI: kerak bo'lsa qisqarib, ichida
     aylanadi — ekran chetidan (telefonda klaviatura ostidan) chiqmaydi. */
  const listMaxHeight = Math.max(
    MIN_LIST_HEIGHT,
    Math.min(maxListHeight, (flip ? above : below) - chrome),
  );
  const minLeft = area.left + EDGE;
  const maxRight = area.left + area.width - EDGE;
  const left = Math.max(minLeft, Math.min(rect.left, maxRight - rect.width));
  return {
    left,
    width: rect.width,
    // Uzun nom panelni kengaytirsa ham u chetdan chiqmaydi.
    maxWidth: Math.max(rect.width, maxRight - left),
    listMaxHeight,
    ...(flip ? { bottom: window.innerHeight - rect.top + GAP } : { top: rect.bottom + GAP }),
  };
};

/**
 * Ro'yxat ochiq turganda joyni kuzatadi: oyna o'lchami, har qanday
 * aylantirish (capture — ichki konteynerlar ham) va telefon klaviaturasi.
 * Tugma ko'rinmay qolsa `onDetach` chaqiriladi.
 */
export const useAnchoredPlacement = ({
  open,
  anchorRef,
  panelRef,
  options,
  onPlace,
  onDetach,
}: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  options: PlacementOptions;
  onPlace: (placement: AnchoredPlacement) => void;
  onDetach: () => void;
}): void => {
  const { maxListHeight, chrome } = options;
  useLayoutEffect(() => {
    if (!open) return;
    const place = (event?: Event): void => {
      // Ro'yxatning o'zini aylantirish joyni o'zgartirmaydi.
      if (event?.target instanceof Node && panelRef.current?.contains(event.target) === true) return;
      const anchor = anchorRef.current;
      if (anchor === null) return;
      if (isDetached(anchor)) {
        onDetach();
        return;
      }
      onPlace(computePlacement(anchor, { maxListHeight, chrome }));
    };
    place();
    const viewport = window.visualViewport;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    // Telefonda klaviatura ochilishi/yopilishi faqat shu yerda seziladi.
    viewport?.addEventListener('resize', place);
    viewport?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      viewport?.removeEventListener('resize', place);
      viewport?.removeEventListener('scroll', place);
    };
    // `onPlace` / `onDetach` har chizishda yangi — ular faqat holat o'rnatadi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorRef, panelRef, maxListHeight, chrome]);
};
